import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Download,
  FileText,
  DollarSign,
  Building,
  Calendar,
} from 'lucide-react';

const PaymentSlips = () => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadSlips();
  }, []);

  const loadSlips = async () => {
    try {
      const res = await api.get('/commission-claims/my-payment-slips');
      setSlips(res.data);
    } catch (error) {
      console.error('Error loading payment slips:', error);
      toast.error('Failed to load payment slips');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (slip) => {
    setDownloading(slip.claim_id);
    try {
      // Remove /api prefix from download_url since api client already adds it
      const apiUrl = slip.download_url.replace(/^\/api/, '');
      const response = await api.get(apiUrl, {
        responseType: 'blob'
      });
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `payment_slip_${slip.claim_ref}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Payment slip downloaded');
    } catch (error) {
      toast.error('Failed to download payment slip');
    } finally {
      setDownloading(null);
    }
  };

  const totalEarned = slips.reduce((sum, slip) => sum + (slip.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-slips-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Slips</h1>
        <p className="text-gray-500">Download your commission payment slips</p>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <DollarSign className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-700">Total Commission Earned</p>
                <p className="text-3xl font-bold text-emerald-600">{formatCurrency(totalEarned)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Payments</p>
              <p className="text-2xl font-bold">{slips.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Slips Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {slips.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No payment slips yet</p>
              <p className="text-sm">Your payment slips will appear here once commissions are processed</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim Ref</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slips.map((slip, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm">{slip.claim_ref}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-gray-400" />
                        {slip.project_name}
                      </div>
                    </TableCell>
                    <TableCell>{slip.unit_number}</TableCell>
                    <TableCell>
                      <span className="font-semibold text-emerald-600">
                        {formatCurrency(slip.amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        {formatDate(slip.paid_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(slip)}
                        disabled={downloading === slip.claim_id}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {downloading === slip.claim_id ? 'Downloading...' : 'Download'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSlips;
